import type { HTMLAttributes, ReactNode } from "react";
import { CircleAlert } from "lucide-react";
import { Alert } from "@/components/alert";
import { ErrorRecoveryHint } from "@/components/error-recovery-hint";
import { Card } from "@/components/ui/card";
import { Spinner } from "@/components/ui/loader";

export function classes(...values: Array<string | false | null | undefined>) {
  return values.filter(Boolean).join(" ");
}

type PageLayoutProps = HTMLAttributes<HTMLElement> & {
  children: ReactNode;
};

export function PageLayout({ children, className, ...props }: PageLayoutProps) {
  return (
    <main className={classes("ux-page", className)} {...props}>
      {children}
    </main>
  );
}

export function PageEmbeddedLayout({
  children,
  className,
  ...props
}: PageLayoutProps) {
  return (
    <div className={classes("ux-page", className)} {...props}>
      {children}
    </div>
  );
}

type PageHeaderProps = {
  eyebrow: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  titleId?: string;
  className?: string;
};

/**
 * One shape for every page: where it sits, what it is, what it is for, and
 * what you can do to it. The action shares the title's row rather than the
 * header's full height, so it lands on the title whatever the eyebrow and
 * description below it are doing.
 */
export function PageHeader({
  eyebrow,
  title,
  description,
  action,
  titleId,
  className,
}: PageHeaderProps) {
  return (
    <header className={classes("ux-page-header", className)}>
      <p className="eyebrow">{eyebrow}</p>
      <div className="ux-page-header-row">
        <h1 id={titleId}>{title}</h1>
        {action ? <div className="ux-page-header-action">{action}</div> : null}
      </div>
      {description ? (
        <p className="ux-page-header-description">{description}</p>
      ) : null}
    </header>
  );
}

type PageGridProps = HTMLAttributes<HTMLDivElement> & {
  children: ReactNode;
};

export function PageGrid({ children, className, ...props }: PageGridProps) {
  return (
    <div className={classes("ux-page-grid", className)} {...props}>
      {children}
    </div>
  );
}

type PageSectionProps = HTMLAttributes<HTMLElement> & {
  children: ReactNode;
};

export function PageSection({
  children,
  className,
  ...props
}: PageSectionProps) {
  return (
    <Card asChild>
      <section
        className={classes("report-card", "ux-page-section", className)}
        {...props}
      >
        {children}
      </section>
    </Card>
  );
}

type PageNoticeProps = HTMLAttributes<HTMLDivElement> & {
  children: ReactNode;
  tone?: "info" | "success" | "warning" | "error";
  /** Something to do about it, drawn as the alert's action. */
  action?: ReactNode;
};

export function PageNotice({
  children,
  className,
  tone = "info",
  role,
  action,
  ...props
}: PageNoticeProps) {
  return (
    <Alert
      {...props}
      tone={tone}
      role={role}
      className={classes("ux-page-notice", className)}
      title={tone === "error" ? children : undefined}
      action={action}
    >
      {tone === "error" ? <ErrorRecoveryHint /> : children}
    </Alert>
  );
}

type PageStateProps = HTMLAttributes<HTMLDivElement> & {
  title?: string;
  /** Optional for an error, whose description is always the recovery hint. */
  description?: string;
  action?: ReactNode;
  loading?: boolean;
  /** Read out but not shown: the page already says the same thing nearby. */
  quiet?: boolean;
  /** A read that failed: the title names it, and the hint says what next. */
  tone?: "error";
};

export function PageState({
  title,
  description,
  action,
  loading = false,
  quiet = false,
  tone,
  className,
  ...props
}: PageStateProps) {
  const error = tone === "error";
  return (
    <div
      className={classes("ux-page-state", error && "is-error", className)}
      role={loading ? "status" : error ? "alert" : undefined}
      {...props}
    >
      {loading ? <Spinner className="size-4 text-muted" /> : null}
      {title ? (
        <h2>
          {error ? (
            <CircleAlert className="ux-page-state-icon" aria-hidden="true" />
          ) : null}
          {title}
        </h2>
      ) : null}
      {description ? (
        <p className={quiet ? "sr-only" : undefined}>{description}</p>
      ) : null}
      {error ? (
        <p>
          <ErrorRecoveryHint />
        </p>
      ) : null}
      {action ? <div className="ux-page-state-action">{action}</div> : null}
    </div>
  );
}
